import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  FormControlLabel,
  ListItemText,
  IconButton,
} from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material';
import {
  Detection,
  DetectionDataset,
  DetectionFlag,
  DetectionObject,
  DetectionObjectType,
} from '../types';
import { TASK_TYPE_CONFIG, getTaskBgColor } from '../constants/taskConfig';

// Constants
const THEME_COLOR = TASK_TYPE_CONFIG.detection.color;

// Color mapping for object types
const OBJECT_TYPE_COLORS: Record<DetectionObjectType, string> = {
  vehicle: '#1976d2', // blue
  bike: '#4caf50', // green
  plate: '#ff9800', // orange
  hazard: '#f44336', // red
};

interface DetectionCardProps {
  detection: Detection;
  imageBaseUrl: string;
  imageWidth: number;
  imageHeight: number;
  isSelected: boolean;
  datasets: DetectionDataset[];
  flags: DetectionFlag[];
  onToggleSelection: (id: string) => void;
  onImageClick: (detection: Detection) => void;
  onFieldChange: (id: string, field: 'dataset' | 'flags', value: string | string[]) => void;
  hideSelection?: boolean;
  onHide?: (id: string) => void;
}

const DetectionCard: React.FC<DetectionCardProps> = ({
  detection,
  imageBaseUrl,
  imageWidth,
  imageHeight,
  isSelected,
  datasets,
  flags,
  onToggleSelection,
  onImageClick,
  onFieldChange,
  hideSelection = false,
  onHide,
}) => {
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement | null>(null);

  // Load image and draw boxes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
      drawImageWithBoxes(ctx, img, detection.objects, imageWidth, imageHeight);
    };
    img.onerror = () => {
      // Draw placeholder on error
      ctx.fillStyle = theme.palette.action.disabledBackground;
      ctx.fillRect(0, 0, imageWidth, imageHeight);
      ctx.fillStyle = theme.palette.text.disabled;
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Image failed to load', imageWidth / 2, imageHeight / 2);
    };
    img.src = `${imageBaseUrl}/${detection.id}?width=${imageWidth}&height=${imageHeight}`;

    // Cleanup: prevent memory leaks by clearing event handlers
    return () => {
      img.onload = null;
      img.onerror = null;
      imageRef.current = null;
      setImageLoaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detection.id, imageBaseUrl, imageWidth, imageHeight]);

  // Redraw when objects change
  useEffect(() => {
    if (!imageLoaded || !imageRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    drawImageWithBoxes(ctx, imageRef.current, detection.objects, imageWidth, imageHeight);
  }, [detection.objects, imageLoaded, imageWidth, imageHeight]);

  const drawImageWithBoxes = (
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    objects: DetectionObject[],
    width: number,
    height: number
  ) => {
    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw image
    ctx.drawImage(img, 0, 0, width, height);

    // Draw bounding boxes
    objects.forEach((obj) => {
      // Skip objects with invalid box data
      if (!obj.box || !Array.isArray(obj.box) || obj.box.length < 4) {
        return;
      }
      const [cx, cy, w, h] = obj.box;

      // Convert proportional coordinates to pixel coordinates
      const pixelX = (cx - w / 2) * width;
      const pixelY = (cy - h / 2) * height;
      const pixelW = w * width;
      const pixelH = h * height;

      const color = OBJECT_TYPE_COLORS[obj.type] || '#ffffff';

      // Draw box
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.strokeRect(pixelX, pixelY, pixelW, pixelH);

      // Draw type label background
      ctx.font = '10px sans-serif';
      const labelWidth = ctx.measureText(obj.type).width + 4;
      ctx.fillStyle = color;
      ctx.fillRect(pixelX, pixelY - 14, labelWidth, 14);

      // Draw type label text
      ctx.fillStyle = '#ffffff';
      ctx.fillText(obj.type, pixelX + 2, pixelY - 3);

      // Draw angle arrow for vehicle/bike objects
      if ((obj.type === 'vehicle' || obj.type === 'bike') && obj.angle !== null && obj.angle !== undefined) {
        const centerX = cx * width;
        const centerY = cy * height;
        const arrowLength = Math.min(pixelW, pixelH) * 0.4;

        // Convert angle to radians (0=right, 90=up)
        const radians = (obj.angle * Math.PI) / 180;
        const endX = centerX + arrowLength * Math.cos(radians);
        const endY = centerY - arrowLength * Math.sin(radians); // Invert Y for screen coords

        // Draw arrow line with dark outline for visibility
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(endX, endY);
        ctx.stroke();

        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(endX, endY);
        ctx.stroke();

        // Draw arrowhead
        const headSize = 8;
        const headAngle = Math.atan2(endY - centerY, endX - centerX);

        ctx.fillStyle = '#000000';
        ctx.beginPath();
        ctx.moveTo(endX, endY);
        ctx.lineTo(
          endX - (headSize + 1) * Math.cos(headAngle - Math.PI / 6),
          endY - (headSize + 1) * Math.sin(headAngle - Math.PI / 6)
        );
        ctx.lineTo(
          endX - (headSize + 1) * Math.cos(headAngle + Math.PI / 6),
          endY - (headSize + 1) * Math.sin(headAngle + Math.PI / 6)
        );
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(endX, endY);
        ctx.lineTo(
          endX - headSize * Math.cos(headAngle - Math.PI / 6),
          endY - headSize * Math.sin(headAngle - Math.PI / 6)
        );
        ctx.lineTo(
          endX - headSize * Math.cos(headAngle + Math.PI / 6),
          endY - headSize * Math.sin(headAngle + Math.PI / 6)
        );
        ctx.closePath();
        ctx.fill();
      }
    });

    // Draw object count badge
    if (objects.length > 0) {
      const badgeText = `${objects.length}`;
      ctx.font = 'bold 12px sans-serif';
      const badgeWidth = ctx.measureText(badgeText).width + 8;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(width - badgeWidth - 4, 4, badgeWidth, 18);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(badgeText, width - badgeWidth, 16);
    }
  };

  return (
    <Card
      elevation={isSelected ? 4 : 2}
      sx={{
        bgcolor: isSelected ? getTaskBgColor('detection', theme.palette.mode) : 'transparent',
        border: isSelected ? `2px solid ${THEME_COLOR}` : 'none',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Selection checkbox and hide button */}
        {!hideSelection && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSelected}
                  onChange={() => onToggleSelection(detection.id)}
                  size="small"
                  sx={{ color: THEME_COLOR, '&.Mui-checked': { color: THEME_COLOR } }}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Select
                </Typography>
              }
            />
            {onHide && (
              <IconButton
                size="small"
                onClick={() => onHide(detection.id)}
                title="Hide item"
                sx={{ ml: 'auto', color: 'text.secondary' }}
              >
                <VisibilityOffIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}

        {/* Detection Image with Boxes */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'center',
            bgcolor: 'action.disabledBackground',
            borderRadius: 1,
            p: 1,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            '&:hover': { bgcolor: 'action.selected' },
          }}
          onClick={() => onImageClick(detection)}
          title="Click to edit objects"
        >
          <canvas
            ref={canvasRef}
            width={imageWidth}
            height={imageHeight}
            style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
          />
        </Box>

        {/* Description (if available) */}
        {detection.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {detection.description}
          </Typography>
        )}

        {/* Dataset (editable) */}
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel>Dataset</InputLabel>
          <Select
            value={detection.dataset.id}
            onChange={(e) => onFieldChange(detection.id, 'dataset', e.target.value)}
            label="Dataset"
          >
            {datasets.map((dataset) => (
              <MenuItem key={dataset.id} value={dataset.id}>
                {dataset.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Flags (editable) */}
        <FormControl fullWidth size="small">
          <InputLabel>Flags</InputLabel>
          <Select
            multiple
            value={detection.flags.map((f) => f.id)}
            onChange={(e) => onFieldChange(detection.id, 'flags', e.target.value as string[])}
            input={<OutlinedInput label="Flags" />}
            renderValue={(selected) =>
              selected
                .map((id) => flags.find((f) => f.id === id)?.label || id)
                .join(', ')
            }
          >
            {flags.map((flag) => (
              <MenuItem key={flag.id} value={flag.id}>
                <Checkbox checked={detection.flags.some((f) => f.id === flag.id)} />
                <ListItemText primary={flag.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </CardContent>
    </Card>
  );
};

export default React.memo(DetectionCard);
